import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useTLang } from 'intl/intl';
import partnerSelector from 'redux/partner/selectors';
import dialogsManagerActions from 'redux/actions/dialogsManager.actions';
import orderDetailDialogActions from 'redux/actions/orderDetailDialog.actions';
import { useGetOrderDetailById } from 'shared/hooks';
import { logAnalyticCIMChatReason } from '../infra';
import { sfOpenFlowExternalObserver } from 'features/order/substitution-flow';

import type { OrderId } from 'shared/domain';

export function useClientInfoModule({ orderId }: Props) {
  const [showReasons, setShowReasons] = useState(false);

  const dispatch = useDispatch();
  const { data: orderDetail } = useGetOrderDetailById(orderId);

  const isRender = isNaN(orderDetail.id);

  const user = orderDetail.user;
  const isMarketplace = orderDetail.deliveryMethod === 'marketplace';

  const { selectIsKanbanUserAppChatEnabled } = partnerSelector;
  const isChatEnabled = useSelector(selectIsKanbanUserAppChatEnabled);
  const showChatBtn = orderDetail.user.chat.isUserAppEnabled && isChatEnabled;
  const showPhoneCallBtn = orderDetail.user.phone.hasProxySession;

  const showPhone = !showPhoneCallBtn && !!user.phone.number;
  const showAddress = isMarketplace && !!user.address.street;

  const showContactInfo = showPhone || showAddress;
  // active billing info in a future release, someone day...
  const showBillingInfo = false; //user.billing.hasDocument;
  const showFooter = showContactInfo || showBillingInfo;

  const handlePhoneCallBtn = () => {
    dialogsManagerActions.openCustomerPhoneProxySessionDialog()(dispatch);
  };

  const handleSupportChatBtn = () => {
    if (orderDetail.substitutionFlowState !== null) handleReasonOther();
    setShowReasons(prevState => !prevState);
  };

  const handleReasonSubstitution = () => {
    logAnalyticCIMChatReason({ orderDetail, reason: 'product_unavailable' });
    sfOpenFlowExternalObserver().dispatch({ orderId });
    setShowReasons(false);
  };

  const handleReasonOther = () => {
    logAnalyticCIMChatReason({ orderDetail, reason: 'other' });
    window.postMessage({
      openUserChat: true,
      orderId,
    });
    setShowReasons(false);
    dispatch(dialogsManagerActions.close());
    dispatch(orderDetailDialogActions.clearOrderDetailDialogAction());
  };

  const lang = {
    header: {
      title: useTLang({ id: 'order.detail.clientModule.title' }),
    },
  };

  return {
    lang,
    isRender,
    isMarketplace,
    user,
    showChatBtn,
    showPhoneCallBtn,
    showReasons,
    showFooter,
    showContactInfo,
    showPhone,
    showAddress,
    showBillingInfo,
    handlePhoneCallBtn,
    handleSupportChatBtn,
    handleReasonSubstitution,
    handleReasonOther,
  };
}

interface Props {
  orderId: OrderId;
}
